// FILE: src/components/QuestionFactory/CaseFactoryPanel.js
//
// "Generate Case Study" mode for the Question Factory (Phase 2 plan Part B,
// PR 8). Self-contained: input form → stepwise generation with a live
// stepper (each step is one edge-function call; progress persists so a
// refresh resumes) → deterministic + AI validation results → ONE automatic
// fix pass over high/medium reviewer findings (regenerate targeted
// artifacts/questions, re-validate; 2026-07-27) → learner-safe preview →
// element-level regeneration → Export bundle / Import as draft.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  createCaseRun, getCaseDraft, listCaseRuns, runCaseStep, setCaseDraftStatus,
} from '../../services/caseFactoryService';
import { adminImportCaseStudy } from '../../services/caseStudyService';
import { downloadJson } from '../case-studies/admin/caseAdminUtils';
import CasePanel from '../case-studies/learner/CasePanel';
import {
  autoFixPlan, bundleSummary, bundleToPanelStages, guidanceForIssue, issueTargets,
  planProgress, regenOptionsForBundle, scopeFromV, stepPlan,
} from './caseFactoryUtils';
import { getActiveCertIds } from '../../config/exams';

const T = {
  line: '#33334e', ink: '#dcdcec', muted: '#8a8aa8', panel: '#232338', bg: '#1a1a2e',
  brand: '#1565c0', good: '#2fbf71', warn: '#e9a23b', bad: '#d83a3a',
};
const S = {
  wrap: { background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, padding: 16, margin: '14px 0', color: T.ink },
  title: { fontSize: '0.95rem', fontWeight: 800, marginBottom: 4 },
  sub: { fontSize: '0.78rem', color: T.muted, marginBottom: 12 },
  label: { display: 'block', fontSize: '0.68rem', fontWeight: 700, color: T.muted, textTransform: 'uppercase', margin: '8px 0 3px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 6, border: `1px solid ${T.line}`, background: T.bg, color: T.ink, fontSize: '0.82rem' },
  btn: { padding: '7px 14px', borderRadius: 7, border: `1px solid ${T.line}`, background: 'rgba(255,255,255,0.06)', color: T.ink, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' },
  btnPrimary: { padding: '7px 14px', borderRadius: 7, border: 'none', background: T.brand, color: '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' },
  err: { background: 'rgba(216,58,58,0.15)', border: `1px solid ${T.bad}`, color: '#ff9b9b', borderRadius: 7, padding: '7px 10px', fontSize: '0.78rem', margin: '8px 0' },
  chip: (c) => ({ display: 'inline-block', padding: '1px 9px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, border: `1px solid ${c}`, color: c, marginRight: 6 }),
};

const DEFAULT_INPUTS = {
  industry: 'Healthcare', difficulty: 'Hard', deliveryContext: 'Hybrid',
  stages: 4, questionsPerStage: 3,
  targetEcoDomains: 'People, Process, Business',
  artifactTypes: 'email, risk_register, status_report, stakeholder_matrix',
  questionTypes: 'multiple_choice, multiple_select, enhanced_matching, ordered_sequence',
  extraInstructions: '',
};

export default function CaseFactoryPanel() {
  const [expanded, setExpanded] = useState(false);
  const [examId, setExamId] = useState('pmp');
  const [provider, setProvider] = useState('claude');
  const [form, setForm] = useState(DEFAULT_INPUTS);
  const [runs, setRuns] = useState([]);
  const [draft, setDraft] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [regenScope, setRegenScope] = useState('');
  const [regenGuidance, setRegenGuidance] = useState('');
  const [autoFix, setAutoFix] = useState(null); // { done, total, label } while the pass runs
  const [autoFixNote, setAutoFixNote] = useState(null); // summary after the pass
  const stopRef = useRef(false);

  const refreshRuns = useCallback(() => {
    listCaseRuns().then(setRuns).catch((e) => setError(e.message));
  }, []);
  useEffect(() => { if (expanded) refreshRuns(); }, [expanded, refreshRuns]);

  function parsedInputs() {
    const csv = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
    return {
      examId,
      industry: form.industry || null,
      difficulty: form.difficulty || null,
      deliveryContext: form.deliveryContext || null,
      targetEcoDomains: csv(form.targetEcoDomains),
      stages: Number(form.stages) || 4,
      questionsPerStage: Number(form.questionsPerStage) || 3,
      artifactTypes: csv(form.artifactTypes),
      questionTypes: csv(form.questionTypes),
      extraInstructions: form.extraInstructions || null,
    };
  }

  /**
   * ONE bounded auto-fix pass after validate lands on needs_review: regenerate
   * every artifact/question a high/medium reviewer finding targets (stage-kind
   * targets stay manual), then re-validate once. Never recurses — whatever the
   * re-validate still flags is handed back for manual review.
   */
  async function autoFixIfNeeded(current) {
    if (stopRef.current || current.status !== 'needs_review') return current;
    const fixes = autoFixPlan(current.consistency_review, current.bundle);
    if (fixes.length === 0) return current;
    for (let i = 0; i < fixes.length; i += 1) {
      if (stopRef.current) return current;
      setAutoFix({ done: i, total: fixes.length, label: fixes[i].label });
      // eslint-disable-next-line no-await-in-loop
      current = await runCaseStep(current.id, 'regenerate', provider,
        { scope: fixes[i].scope, guidance: fixes[i].guidance });
      setDraft(current);
    }
    setAutoFix({ done: fixes.length, total: fixes.length, label: 'Re-validating' });
    current = await runCaseStep(current.id, 'validate', provider, {});
    setDraft(current);
    setAutoFixNote(`Auto-fix regenerated ${fixes.length} element${fixes.length === 1 ? '' : 's'} flagged high/medium by the reviewer — anything still listed below needs manual review.`);
    return current;
  }

  /** Run all remaining steps sequentially; stoppable; resumable. */
  async function generate(draftRow) {
    setRunning(true); setError(null); setAutoFixNote(null); stopRef.current = false;
    let current = draftRow;
    try {
      const plan = stepPlan(current.inputs);
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (stopRef.current) break;
        const { next } = planProgress(plan, current.step_status || {});
        if (!next) break;
        // eslint-disable-next-line no-await-in-loop
        current = await runCaseStep(current.id, next.invoke.step, provider,
          next.invoke.stage ? { stage: next.invoke.stage } : {});
        setDraft(current);
      }
      current = await autoFixIfNeeded(current);
      refreshRuns();
    } catch (e) { setError(e.message); }
    finally { setRunning(false); setAutoFix(null); }
  }

  async function start() {
    setError(null);
    try {
      const inputs = parsedInputs();
      const res = await createCaseRun(examId, provider, inputs);
      const row = await getCaseDraft(res.draft_id);
      setDraft(row);
      await generate(row);
    } catch (e) { setError(e.message); }
  }

  async function regenerate() {
    if (!draft || !regenScope) return;
    setRunning(true); setError(null);
    try {
      const scope = scopeFromV(regenScope);
      let current = await runCaseStep(draft.id, 'regenerate', provider, { scope, guidance: regenGuidance || null });
      current = await runCaseStep(current.id, 'validate', provider, {});
      setDraft(current); setRegenGuidance('');
    } catch (e) { setError(e.message); }
    finally { setRunning(false); }
  }

  async function importDraft() {
    setError(null);
    try {
      const res = await adminImportCaseStudy(draft.bundle, 'create');
      await setCaseDraftStatus(draft.id, 'imported', res.case_study_id);
      setDraft(await getCaseDraft(draft.id));
      refreshRuns();
    } catch (e) { setError(e.message); }
  }

  const summary = draft && draft.bundle ? bundleSummary(draft.bundle) : null;
  const plan = draft ? stepPlan(draft.inputs) : [];
  const progress = draft ? planProgress(plan, draft.step_status || {}) : null;
  const regenOptions = draft && draft.bundle ? regenOptionsForBundle(draft.bundle) : [];

  /** Pre-fill the regeneration controls from a reviewer finding. */
  function targetIssue(issue, option) {
    setRegenScope(option.v);
    setRegenGuidance(guidanceForIssue(issue));
  }

  return (
    <div style={S.wrap} data-testid="case-factory-panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div>
          <div style={S.title}>🗂️ Generate Case Study</div>
          <div style={{ ...S.sub, marginBottom: 0 }}>
            Stepwise generation → validation → preview → import as a draft case (never auto-published).
          </div>
        </div>
        <span style={{ flex: 1 }} />
        <button style={S.btn} data-testid="case-factory-toggle" onClick={() => setExpanded((e) => !e)}>
          {expanded ? 'Hide ▲' : 'Open ▼'}
        </button>
      </div>

      {expanded && (<>
        {error && <div style={S.err} data-testid="case-factory-error">{error}</div>}

        {!draft && (<>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 14px' }}>
            <div><label style={S.label}>Certification</label>
              <select style={S.input} data-testid="cf-exam" value={examId} onChange={(e) => setExamId(e.target.value)}>
                {getActiveCertIds().map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
              </select></div>
            <div><label style={S.label}>Provider</label>
              <select style={S.input} data-testid="cf-provider" value={provider} onChange={(e) => setProvider(e.target.value)}>
                <option value="claude">Claude</option><option value="chatgpt">ChatGPT</option><option value="gemini">Gemini</option>
              </select></div>
            <div><label style={S.label}>Industry</label>
              <input style={S.input} data-testid="cf-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></div>
            <div><label style={S.label}>Difficulty</label>
              <select style={S.input} value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })}>
                {['Easy', 'Medium', 'Hard', 'Expert'].map((d) => <option key={d}>{d}</option>)}
              </select></div>
            <div><label style={S.label}>Delivery context</label>
              <select style={S.input} value={form.deliveryContext} onChange={(e) => setForm({ ...form, deliveryContext: e.target.value })}>
                {['Predictive', 'Agile', 'Hybrid'].map((d) => <option key={d}>{d}</option>)}
              </select></div>
            <div><label style={S.label}>Stages</label>
              <input style={S.input} type="number" min="2" max="6" data-testid="cf-stages" value={form.stages} onChange={(e) => setForm({ ...form, stages: e.target.value })} /></div>
            <div><label style={S.label}>Questions per stage</label>
              <input style={S.input} type="number" min="1" max="4" value={form.questionsPerStage} onChange={(e) => setForm({ ...form, questionsPerStage: e.target.value })} /></div>
            <div><label style={S.label}>Target domains (csv)</label>
              <input style={S.input} value={form.targetEcoDomains} onChange={(e) => setForm({ ...form, targetEcoDomains: e.target.value })} /></div>
            <div><label style={S.label}>Artifact types (csv)</label>
              <input style={S.input} value={form.artifactTypes} onChange={(e) => setForm({ ...form, artifactTypes: e.target.value })} /></div>
            <div><label style={S.label}>Question types (csv)</label>
              <input style={S.input} value={form.questionTypes} onChange={(e) => setForm({ ...form, questionTypes: e.target.value })} /></div>
          </div>
          <label style={S.label}>Extra instructions</label>
          <textarea style={{ ...S.input, minHeight: 54 }} value={form.extraInstructions} onChange={(e) => setForm({ ...form, extraInstructions: e.target.value })} />
          <div style={{ marginTop: 12 }}>
            <button style={S.btnPrimary} data-testid="cf-generate" disabled={running} onClick={start}>
              {running ? 'Generating…' : 'Generate case study'}
            </button>
          </div>

          {runs.length > 0 && (<>
            <label style={S.label}>Previous case runs</label>
            {runs.slice(0, 6).map((r) => (
              <div key={r.draft_id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', borderBottom: `1px solid ${T.line}`, fontSize: '0.78rem' }}>
                <span style={S.chip(r.status === 'imported' ? T.good : r.status === 'validated' ? T.brand : T.warn)}>{r.status}</span>
                <span>{r.title || '(untitled)'}</span>
                <span style={{ color: T.muted }}>{(r.exam_id || '').toUpperCase()} · {r.source} · ${Number(r.estimated_cost_usd || 0).toFixed(2)}</span>
                <span style={{ flex: 1 }} />
                <button style={S.btn} data-testid={`cf-open-${r.draft_id}`}
                  onClick={async () => { setDraft(await getCaseDraft(r.draft_id)); }}>Open</button>
              </div>
            ))}
          </>)}
        </>)}

        {draft && (<>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '10px 0' }}>
            <button style={S.btn} onClick={() => { stopRef.current = true; setDraft(null); setShowPreview(false); setAutoFixNote(null); refreshRuns(); }}>← All runs</button>
            <strong data-testid="cf-draft-title">{summary && summary.title ? summary.title : 'Generating…'}</strong>
            <span style={S.chip(draft.status === 'validated' ? T.good : draft.status === 'imported' ? T.good : T.warn)}>{draft.status}</span>
            {summary && <span style={{ color: T.muted, fontSize: '0.76rem' }}>
              {summary.stages} stages · {summary.artifacts} artifacts · {summary.questions} questions · ${Number(draft.estimated_cost_usd || 0).toFixed(2)}
            </span>}
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }} data-testid="cf-stepper">
            {plan.map((p) => {
              const done = progress && progress.doneKeys.has(p.key);
              const isNext = progress && progress.next && progress.next.key === p.key;
              return (
                <span key={p.key} style={S.chip(done ? T.good : isNext && running ? T.brand : T.muted)}>
                  {done ? '✓ ' : isNext && running ? '… ' : ''}{p.label}
                </span>
              );
            })}
          </div>
          {progress && progress.next && !running && draft.status !== 'imported' && (
            <button style={S.btnPrimary} data-testid="cf-resume" onClick={() => generate(draft)}>
              Resume generation ({progress.doneCount}/{progress.total} steps done)
            </button>
          )}
          {autoFix && (
            <div data-testid="cf-autofix" style={{ color: T.warn, fontSize: '0.78rem', margin: '8px 0' }}>
              ⟳ Auto-fixing reviewer findings ({Math.min(autoFix.done + 1, autoFix.total)}/{autoFix.total}): {autoFix.label}
            </div>
          )}
          {!autoFix && autoFixNote && (
            <div data-testid="cf-autofix-done" style={{ color: T.muted, fontSize: '0.76rem', margin: '8px 0' }}>
              ✓ {autoFixNote}
            </div>
          )}

          {draft.validation && (
            <div style={{ margin: '10px 0' }} data-testid="cf-validation">
              <label style={S.label}>Deterministic checks</label>
              {(draft.validation.errors || []).length === 0
                ? <span style={S.chip(T.good)}>structure OK</span>
                : (draft.validation.errors || []).map((e, i) => <div key={i} style={S.err}>✕ {e}</div>)}
              {(draft.validation.warnings || []).map((w, i) => (
                <div key={i} style={{ color: T.warn, fontSize: '0.76rem' }}>⚠ {w}</div>))}
            </div>
          )}
          {draft.consistency_review && (
            <div style={{ margin: '10px 0' }} data-testid="cf-consistency">
              <label style={S.label}>AI consistency review — overall {draft.consistency_review.overall}/10 · {draft.consistency_review.recommendation}</label>
              {(draft.consistency_review.issues || []).map((iss, i) => {
                const sevColor = iss.severity === 'high' ? T.bad : iss.severity === 'medium' ? T.warn : T.muted;
                const targets = draft.bundle ? issueTargets(iss, draft.bundle) : [];
                return (
                  <div key={i} data-testid={`cf-issue-${i}`}
                    style={{ borderLeft: `3px solid ${sevColor}`, padding: '5px 8px', margin: '6px 0', background: 'rgba(255,255,255,0.03)', borderRadius: '0 6px 6px 0' }}>
                    <div style={{ fontSize: '0.76rem', color: iss.severity === 'high' ? '#ff9b9b' : iss.severity === 'medium' ? T.warn : T.muted }}>
                      <span style={S.chip(sevColor)}>{iss.severity}</span>
                      <strong>{iss.where}</strong> — {iss.detail}
                    </div>
                    {iss.suggested_fix && (
                      <div style={{ fontSize: '0.74rem', color: T.muted, marginTop: 3 }}>
                        Suggested fix: {iss.suggested_fix}
                      </div>
                    )}
                    {draft.status !== 'imported' && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 5 }}>
                        {targets.length === 0 && (
                          <span style={{ fontSize: '0.72rem', color: T.muted }}>
                            No single element matched — pick a scope in “Regenerate an element…” below.
                          </span>
                        )}
                        {targets.map((o) => (
                          <button key={o.v} style={{ ...S.btn, padding: '3px 10px', fontSize: '0.72rem' }}
                            data-testid={`cf-issue-target-${i}-${o.v}`}
                            title={`Pre-fill regeneration of ${o.label} with this finding as guidance`}
                            onClick={() => targetIssue(iss, o)}>
                            ↻ {o.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {draft.bundle && draft.status !== 'imported' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap', margin: '10px 0' }}>
              <select style={{ ...S.input, width: 'auto', maxWidth: 420 }} data-testid="cf-regen-scope" value={regenScope} onChange={(e) => setRegenScope(e.target.value)}>
                <option value="">Regenerate an element…</option>
                {regenOptions.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
              <textarea style={{ ...S.input, width: 320, minHeight: 34 }} data-testid="cf-regen-guidance"
                placeholder="Optional guidance for the rewrite (a ↻ button on a finding above fills this in)"
                value={regenGuidance} onChange={(e) => setRegenGuidance(e.target.value)} />
              <button style={S.btn} data-testid="cf-regen" disabled={running || !regenScope} onClick={regenerate}>
                {running ? 'Working…' : 'Regenerate'}
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0' }}>
            {draft.bundle && (
              <button style={S.btn} data-testid="cf-preview-toggle" onClick={() => setShowPreview((p) => !p)}>
                {showPreview ? 'Hide preview ▲' : 'Preview case ▼'}
              </button>
            )}
            {draft.bundle && (
              <button style={S.btn} data-testid="cf-export"
                onClick={() => downloadJson(`case-${(draft.bundle.case && draft.bundle.case.slug) || draft.id}.json`, draft.bundle)}>
                ⇩ Export bundle
              </button>
            )}
            {draft.bundle && draft.status === 'validated' && (
              <button style={S.btnPrimary} data-testid="cf-import" onClick={importDraft}>
                Import as draft case →
              </button>
            )}
            {draft.status === 'imported' && <span style={S.chip(T.good)}>imported — open it in Case Studies</span>}
          </div>

          {showPreview && draft.bundle && (
            <div style={{ background: '#f6f7fb', color: '#1a2138', borderRadius: 10, padding: 14, maxHeight: '52vh', overflowY: 'auto' }} data-testid="cf-preview">
              <CasePanel
                caseMeta={{ title: draft.bundle.case ? draft.bundle.case.title : '', learner_role: draft.bundle.case ? draft.bundle.case.learner_role : '' }}
                stages={bundleToPanelStages(draft.bundle)}
                latestStage={null}
              />
            </div>
          )}
        </>)}
      </>)}
    </div>
  );
}
